// Terrain Advisor settings (ForeFlight-style).
//
// Persists user choices for the per-pixel TAWS overlay:
//   - which preset (Normal / Low / Heli Normal / Heli Medium / Heli Low)
//   - or fully custom red / yellow AGL thresholds
//   - activation ground speed (suppress hazard color when stationary)
//   - corridor mode + corridor width (limit shading to the active
//     route corridor when set to 'auto'; show everywhere otherwise)
//
// All thresholds are stored in FEET (AGL relative to aircraft); the
// MapView effect converts to meters for the color-relief expression.
//
// When avionics input is wired later, the activation gate keeps the
// same shape \u2014 read groundSpeedKt from the GPS store (or whatever
// avionics adapter we add), which already exposes it.
//
// Persistence: localStorage. Versioned key so we can re-migrate later.

import { create } from 'zustand';

export type TerrainPresetId =
  | 'normal'      //  100 / 1000 ft
  | 'low'         //  100 /  500 ft  (default for fixed-wing low altitude ops)
  | 'heli-normal' //   50 /  300 ft
  | 'heli-medium' //   25 /  200 ft
  | 'heli-low'    //   25 /  100 ft
  | 'custom';

export type CorridorMode = 'auto' | 'show' | 'hide';

export interface TerrainPreset {
  id: TerrainPresetId;
  label: string;
  redFt: number;
  yellowFt: number;
}

export const TERRAIN_PRESETS: Record<Exclude<TerrainPresetId, 'custom'>, TerrainPreset> = {
  'normal':      { id: 'normal',      label: 'Normal',         redFt: 100, yellowFt: 1000 },
  'low':         { id: 'low',         label: 'Low',            redFt: 100, yellowFt:  500 },
  'heli-normal': { id: 'heli-normal', label: 'Heli \u2014 Normal', redFt:  50, yellowFt:  300 },
  'heli-medium': { id: 'heli-medium', label: 'Heli \u2014 Medium', redFt:  25, yellowFt:  200 },
  'heli-low':    { id: 'heli-low',    label: 'Heli \u2014 Low',    redFt:  25, yellowFt:  100 },
};

interface TerrainAdvisorState {
  presetId: TerrainPresetId;
  /** Effective hazard buffer below aircraft \u2014 terrain within this
   *  many feet (or above) is painted RED. */
  redFt: number;
  /** Effective hazard buffer below aircraft \u2014 terrain within this
   *  many feet (or above) is painted YELLOW. Must be \u2265 redFt. */
  yellowFt: number;
  /** Below this ground speed (knots) the advisor is suppressed.
   *  Default 30 kt \u2014 below typical helicopter taxi / hover. */
  activationSpeedKt: number;
  /** Corridor display mode for the colored overlay:
   *  - 'auto'  = only paint terrain within `corridorWidthNm` of the
   *              active flight plan polyline. Cleaner when enroute.
   *  - 'show'  = paint everywhere on the visible map. (Default.)
   *  - 'hide'  = TAWS overlay is turned off entirely (hillshade keeps
   *              rendering for relief reference). */
  corridorMode: CorridorMode;
  /** Half-width of the route corridor in NM. 10 \u2192 a 20 nm-wide strip. */
  corridorWidthNm: number;
  /** Master kill-switch for terrain alert toasts. When false, NO terrain
   *  proximity alerts are pushed \u2014 only the colored map overlay is
   *  affected by corridorMode. Default true (alerts on). Persisted. */
  alertsEnabled: boolean;

  setPreset:           (id: TerrainPresetId) => void;
  setCustomThresholds: (redFt: number, yellowFt: number) => void;
  setActivationSpeedKt:(kt: number) => void;
  setCorridorMode:     (m: CorridorMode) => void;
  setCorridorWidthNm:  (nm: number) => void;
  setAlertsEnabled:    (v: boolean) => void;
  hydrate:             () => void;
  /** Called by uiStore when server ui_prefs has a terrain sub-key.
   *  Applies server values without writing back to localStorage
   *  (uiStore owns the round-trip). */
  applyServerPrefs: (p: Partial<{
    presetId: string;
    redFt: number;
    yellowFt: number;
    activationSpeedKt: number;
    corridorMode: string;
    corridorWidthNm: number;
    alertsEnabled: boolean;
  }>) => void;
}

const LS_KEY = 'fyrefly.terrainAdvisor.v1';

interface Persisted {
  presetId: TerrainPresetId;
  redFt: number;
  yellowFt: number;
  activationSpeedKt: number;
  corridorMode: CorridorMode;
  corridorWidthNm: number;
  alertsEnabled: boolean;
}

function defaultState(): Persisted {
  const p = TERRAIN_PRESETS['low'];
  return {
    presetId: p.id,
    redFt: p.redFt,
    yellowFt: p.yellowFt,
    activationSpeedKt: 30,
    corridorMode: 'show',
    corridorWidthNm: 10,
    alertsEnabled: true,
  };
}

function readPersisted(): Persisted {
  if (typeof localStorage === 'undefined') return defaultState();
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw) as Partial<Persisted>;
    const d = defaultState();
    return {
      presetId:           (parsed.presetId ?? d.presetId) as TerrainPresetId,
      redFt:              Number.isFinite(parsed.redFt as any) ? (parsed.redFt as number) : d.redFt,
      yellowFt:           Number.isFinite(parsed.yellowFt as any) ? (parsed.yellowFt as number) : d.yellowFt,
      activationSpeedKt:  Number.isFinite(parsed.activationSpeedKt as any) ? (parsed.activationSpeedKt as number) : d.activationSpeedKt,
      corridorMode:       (parsed.corridorMode ?? d.corridorMode) as CorridorMode,
      corridorWidthNm:    Number.isFinite(parsed.corridorWidthNm as any) ? (parsed.corridorWidthNm as number) : d.corridorWidthNm,
      alertsEnabled:      typeof parsed.alertsEnabled === 'boolean' ? parsed.alertsEnabled : d.alertsEnabled,
    };
  } catch {
    return defaultState();
  }
}

function persist(state: Persisted): void {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch {}
}

export const useTerrainAdvisorStore = create<TerrainAdvisorState>((set, get) => ({
  ...defaultState(),

  setPreset: (id) => {
    if (id === 'custom') {
      set({ presetId: 'custom' });
      const s = get();
      persist({
        presetId: 'custom',
        redFt: s.redFt,
        yellowFt: s.yellowFt,
        activationSpeedKt: s.activationSpeedKt,
        corridorMode: s.corridorMode,
        corridorWidthNm: s.corridorWidthNm,
        alertsEnabled: s.alertsEnabled,
      });
      return;
    }
    const p = TERRAIN_PRESETS[id];
    if (!p) return;
    set({ presetId: id, redFt: p.redFt, yellowFt: p.yellowFt });
    const s = get();
    persist({
      presetId: id,
      redFt: p.redFt,
      yellowFt: p.yellowFt,
      activationSpeedKt: s.activationSpeedKt,
      corridorMode: s.corridorMode,
      corridorWidthNm: s.corridorWidthNm,
      alertsEnabled: s.alertsEnabled,
    });
  },

  setCustomThresholds: (redFt, yellowFt) => {
    const r = Math.max(0, Math.round(redFt));
    const y = Math.max(r, Math.round(yellowFt));
    set({ presetId: 'custom', redFt: r, yellowFt: y });
    const s = get();
    persist({
      presetId: 'custom', redFt: r, yellowFt: y,
      activationSpeedKt: s.activationSpeedKt,
      corridorMode: s.corridorMode,
      corridorWidthNm: s.corridorWidthNm,
      alertsEnabled: s.alertsEnabled,
    });
  },

  setActivationSpeedKt: (kt) => {
    const v = Math.max(0, Math.round(kt));
    set({ activationSpeedKt: v });
    const s = get();
    persist({
      presetId: s.presetId, redFt: s.redFt, yellowFt: s.yellowFt,
      activationSpeedKt: v,
      corridorMode: s.corridorMode,
      corridorWidthNm: s.corridorWidthNm,
      alertsEnabled: s.alertsEnabled,
    });
  },

  setCorridorMode: (m) => {
    set({ corridorMode: m });
    const s = get();
    persist({
      presetId: s.presetId, redFt: s.redFt, yellowFt: s.yellowFt,
      activationSpeedKt: s.activationSpeedKt,
      corridorMode: m,
      corridorWidthNm: s.corridorWidthNm,
      alertsEnabled: s.alertsEnabled,
    });
  },

  setCorridorWidthNm: (nm) => {
    const v = Math.max(1, Math.round(nm));
    set({ corridorWidthNm: v });
    const s = get();
    persist({
      presetId: s.presetId, redFt: s.redFt, yellowFt: s.yellowFt,
      activationSpeedKt: s.activationSpeedKt,
      corridorMode: s.corridorMode,
      corridorWidthNm: v,
      alertsEnabled: s.alertsEnabled,
    });
  },

  setAlertsEnabled: (v) => {
    set({ alertsEnabled: v });
    const s = get();
    persist({
      presetId: s.presetId, redFt: s.redFt, yellowFt: s.yellowFt,
      activationSpeedKt: s.activationSpeedKt,
      corridorMode: s.corridorMode,
      corridorWidthNm: s.corridorWidthNm,
      alertsEnabled: v,
    });
  },

  hydrate: () => {
    const p = readPersisted();
    set(p);
  },

  applyServerPrefs: (p) => {
    const s = get();
    set({
      presetId: (p.presetId as TerrainPresetId) ?? s.presetId,
      redFt: p.redFt ?? s.redFt,
      yellowFt: p.yellowFt ?? s.yellowFt,
      activationSpeedKt: p.activationSpeedKt ?? s.activationSpeedKt,
      corridorMode: (p.corridorMode as CorridorMode) ?? s.corridorMode,
      corridorWidthNm: p.corridorWidthNm ?? s.corridorWidthNm,
      alertsEnabled: p.alertsEnabled ?? s.alertsEnabled,
    });
  },
}));

/** Returns the current terrain prefs object for embedding in ui_prefs blob.
 *  Called by uiStore.pushUiPrefsToServer. */
export function getTerrainPrefsForSync() {
  const s = useTerrainAdvisorStore.getState();
  return {
    presetId: s.presetId,
    redFt: s.redFt,
    yellowFt: s.yellowFt,
    activationSpeedKt: s.activationSpeedKt,
    corridorMode: s.corridorMode,
    corridorWidthNm: s.corridorWidthNm,
    alertsEnabled: s.alertsEnabled,
  };
}
